import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';

export const CCF_COPILOT_KEY = 'copilot';

export interface rtig {
  sentimentScore: number;
  sentimentText: string;
}

export interface suggestionsCard {
  msgType: string;
  suggestionLabel?: string;
  message: string;
  imgSrc?: string;
  sentiment?: string;
  showIcon: boolean;
  isSelectedAsQuickReply?: boolean;
  buttonCaption?: string;
}

export interface copilotAssetType {
  webLinks: any;
  images: any;
  processes: any;
}

export interface CcfcopilotState {
  flowMsgIndex: number;
  showConversationTopics: boolean;
  conversationTopics: any;
  isDamagedLuggagePhotoUploaded: boolean;
  isReimbursementClaimApproved: boolean;
  isCopilotSummarySubmitted: boolean;
  loadAutoSummaryState: boolean;
  copilotAppSpaceArticle: any;
  sendCopilotSuggestionToConversionTray: any;
  sendCopilotSuggestionToReplyBox: boolean;
  isAssetsAddedToCopilotSuggestion: boolean;
  disableCopilotSuggestionCardSent: boolean;
  copilotCardListForChat: any[];
  copilotCardListForVideoCall: any[];
  copilotCardListForVoice: any[];
  copilotCardListForEmail: any[];
  spaceBtnCounterForCopilotCard: number;
  spaceBtnCounterForCopilotChatMessage: number;
  spaceBtnCounterForCopilotVoice: number;
  spaceBtnCounterForCopilotEmail: number;
  showSummaryLoadingAnimation: boolean;
  showAgentsForInteractionTransfer: boolean;
  isSuggestionClicked: boolean;
  noOfMsgInCopilotChat: number;
  sentiment: rtig;
  selectedCopilotAssets: copilotAssetType;
  loadCopilotInteractionAnimation?: boolean;
}

export const initialCcfcopilotState: CcfcopilotState = {
  flowMsgIndex: 0,
  showConversationTopics: true,
  conversationTopics: [],
  isDamagedLuggagePhotoUploaded: false,
  isReimbursementClaimApproved: false,
  isCopilotSummarySubmitted: false,
  loadAutoSummaryState: false,
  copilotAppSpaceArticle: [],
  sendCopilotSuggestionToConversionTray: {},
  sendCopilotSuggestionToReplyBox: false,
  isAssetsAddedToCopilotSuggestion: false,
  disableCopilotSuggestionCardSent: false,
  copilotCardListForChat: [],
  copilotCardListForVideoCall: [],
  copilotCardListForVoice: [],
  copilotCardListForEmail: [],
  spaceBtnCounterForCopilotCard: 0,
  spaceBtnCounterForCopilotChatMessage: 0,
  spaceBtnCounterForCopilotVoice: 0,
  spaceBtnCounterForCopilotEmail: 0,
  showSummaryLoadingAnimation: false,
  showAgentsForInteractionTransfer: false,
  isSuggestionClicked: false,
  noOfMsgInCopilotChat: 0,
  sentiment: {
    sentimentScore: 0,
    sentimentText: 'The Customer reported that their washer has a foul smell.',
  },
  selectedCopilotAssets: {
    webLinks: [],
    images: [],
    processes: [],
  },
  loadCopilotInteractionAnimation: false,
};

export const copilotSlice = createSlice({
  name: CCF_COPILOT_KEY,
  initialState: initialCcfcopilotState,
  reducers: {
    /**
     * updated spaceBtnClickedCount
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateSpaceBtnClickedCount(state, action))`
     */
    updateFlowMsgIndex(state, action: PayloadAction<number>) {
      return {
        ...state,
        flowMsgIndex: action.payload,
      };
    },
    /**
     * Function to check if Damaged Luggage Photo is uploaded or not
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setIsDamagedLuggagePhotoUploaded(true));
     */
    setIsDamagedLuggagePhotoUploaded(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isDamagedLuggagePhotoUploaded: action.payload,
      };
    },

    /**
     * updated updateCoversationTopics
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateCoversationTopics(state, action))`
     */
    updateCoversationTopics(state, action: PayloadAction<any>) {
      return {
        ...state,
        conversationTopics: action.payload,
      };
    },

    /**
     * updated spaceBtnClickedCount
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateSpaceBtnClickedCount(state, action))`
     */
    updateCopilotAppSpaceArticle(state, action: PayloadAction<any>) {
      return {
        ...state,
        copilotAppSpaceArticle: action.payload,
      };
    },

    /**
     * Function to check if Reimbursement claim is approved or not
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setIsReimbursementClaimApproved(true));
     */
    setIsReimbursementClaimApproved(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isReimbursementClaimApproved: action.payload,
      };
    },
    /**
     * Function to check if ConversationTopics are hidden or not
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setshowConversationTopics(true));
     */
    setshowConversationTopics(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        showConversationTopics: action.payload,
      };
    },

    /**
     * update state of load auto summary flag
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateLoadAutoSummary(state, action))`
     */
    updateLoadAutoSummary(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        loadAutoSummaryState: action.payload,
      };
    },

    /**
     * update state of send copilot suggestion To Agent Reply Box
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateSendCopilotSuggestionToReplyBox(state, action))`
     */
    updateSendCopilotSuggestionToReplyBox(
      state,
      action: PayloadAction<boolean>
    ) {
      return {
        ...state,
        sendCopilotSuggestionToReplyBox: action.payload,
      };
    },

    /**
     * update state of copilot suggestion to chat converstion tray
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateSendCopilotSuggestionToConversionTray(state, action))`
     */
    updateSendCopilotSuggestionToConversionTray(
      state,
      action: PayloadAction<any>
    ) {
      return {
        ...state,
        sendCopilotSuggestionToConversionTray: action.payload,
      };
    },

    /**
     * updated copilot Card List for Video call
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateCopilotCardList(state, action))`
     */
    updateCopilotCardListForVideoCall(
      state,
      action: PayloadAction<{ data: any; type: string }>
    ) {
      return {
        ...state,
        copilotCardListForVideoCall:
          action.payload.type === 'reset'
            ? action.payload.data
            : [...state.copilotCardListForVideoCall, action.payload.data],
      };
    },

    /**
     * updated copilot Card List for Video call
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateCopilotCardList(state, action))`
     */
    updateCopilotCardListForVoice(
      state,
      action: PayloadAction<{ data: any; type: string }>
    ) {
      return {
        ...state,
        copilotCardListForVoice:
          action.payload.type === 'reset'
            ? action.payload.data
            : [...state.copilotCardListForVoice, action.payload.data],
      };
    },

    /**
     * updated copilot Card List for Email
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateCopilotCardListForEmail(state, action))`
     */
    updateCopilotCardListForEmail(
      state,
      action: PayloadAction<{ data: any; type: string }>
    ) {
      return {
        ...state,
        copilotCardListForEmail:
          action.payload.type === 'reset'
            ? action.payload.data
            : [...state.copilotCardListForEmail, action.payload.data],
      };
    },

    /**
     * Function to check if Copilot Summary is submitted or not
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setIsCopilotSummarySubmitted(true));
     */
    setIsCopilotSummarySubmitted(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isCopilotSummarySubmitted: action.payload,
      };
    },
    /**
     * Function to check if Copilot suggestion has assets added or not
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setIsAssetsAddedToCopilotSuggestion(true));
     */
    setIsAssetsAddedToCopilotSuggestion(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isAssetsAddedToCopilotSuggestion: action.payload,
      };
    },

    /**
     * Function to trigger copilot interaction animation
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setLoadCopilotInteractionAnimation(true));
     */
    setLoadCopilotInteractionAnimation(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        loadCopilotInteractionAnimation: action.payload,
      };
    },

    /**
     * Function to check if suggestion is Clicked or not
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setIsSuggestionClicked(true));
     */
    setIsSuggestionClicked(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isSuggestionClicked: action.payload,
      };
    },
    /**
     * update state of load auto summary Animation flag
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(setShowSummaryLoadingAnimation(state, action))`
     */
    setShowSummaryLoadingAnimation(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        showSummaryLoadingAnimation: action.payload,
      };
    },
    /**
     * updated spaceBtnClickedCount
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateSpaceBtnClickedCount(state, action))`
     */
    updateSpaceBtnCounterForCopilotCard(state, action: PayloadAction<number>) {
      return {
        ...state,
        spaceBtnCounterForCopilotCard: action.payload,
      };
    },

    /**
     * updated spaceBtnCounterForCopilotVoice
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateSpaceBtnCounterForCopilotVoice(state, action))`
     */
    updateSpaceBtnCounterForCopilotVoice(state, action: PayloadAction<number>) {
      return {
        ...state,
        spaceBtnCounterForCopilotVoice: action.payload,
      };
    },

    /**
     * updated spaceBtnCounterForCopilotEmail
     * @param state - copilot state
     * @param action - `any`
     * @example - `dispatch(updateSpaceBtnCounterForCopilotEmail(state, action))`
     */
    updateSpaceBtnCounterForCopilotEmail(state, action: PayloadAction<number>) {
      return {
        ...state,
        spaceBtnCounterForCopilotEmail: action.payload,
      };
    },

    /**
     * Function to store selected copilot assets
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setSelectedCopilotAssets());
     */
    setSelectedCopilotAssets(state, action: PayloadAction<copilotAssetType>) {
      return {
        ...state,
        selectedCopilotAssets: action.payload,
      };
    },
  },
});

export const copilotReducer = copilotSlice.reducer;
export const {
  updateFlowMsgIndex,
  setIsDamagedLuggagePhotoUploaded,
  setIsReimbursementClaimApproved,
  updateCopilotAppSpaceArticle,
  setIsCopilotSummarySubmitted,
  updateLoadAutoSummary,
  updateSendCopilotSuggestionToConversionTray,
  updateSendCopilotSuggestionToReplyBox,
  setIsAssetsAddedToCopilotSuggestion,
  updateCopilotCardListForVideoCall,
  updateCopilotCardListForVoice,
  updateCopilotCardListForEmail,
  setIsSuggestionClicked,
  setShowSummaryLoadingAnimation,
  updateSpaceBtnCounterForCopilotCard,
  updateSpaceBtnCounterForCopilotVoice,
  updateSpaceBtnCounterForCopilotEmail,
  setSelectedCopilotAssets,
  setLoadCopilotInteractionAnimation,
  setshowConversationTopics,
  updateCoversationTopics,
} = copilotSlice.actions;
export default copilotSlice.reducer;

/**
 * used to getcopilotState
 * @param rootState - copilot state
 * @example - const copilotState = getcopilotState(state)
 */
const getcopilotState = (rootState: { copilot: CcfcopilotState }) => {
  return rootState[CCF_COPILOT_KEY];
};

export const getcopilotFlowMsgIndex = createSelector(
  getcopilotState,
  (state) => state?.flowMsgIndex
);

export const getIsDamagedLuggagePhotoUploaded = createSelector(
  getcopilotState,
  (state) => state.isDamagedLuggagePhotoUploaded
);

export const getIsReimbursementClaimApproved = createSelector(
  getcopilotState,
  (state) => state.isReimbursementClaimApproved
);

export const getCopilotAppSpaceArticle = createSelector(
  getcopilotState,
  (state) => state.copilotAppSpaceArticle
);

export const getCopilotSuggestionToConversionTrayState = createSelector(
  getcopilotState,
  (state) => state.sendCopilotSuggestionToConversionTray
);

export const getCopilotSuggestionToReplyBoxState = createSelector(
  getcopilotState,
  (state) => state.sendCopilotSuggestionToReplyBox
);

export const getIsAssetsAddedToCopilotSuggestionState = createSelector(
  getcopilotState,
  (state) => state.isAssetsAddedToCopilotSuggestion
);

export const getCopilotCardListForChat = createSelector(
  getcopilotState,
  (state) => state?.copilotCardListForChat
);

export const getCopilotCardListForVideoCall = createSelector(
  getcopilotState,
  (state) => state?.copilotCardListForVideoCall
);

export const getCopilotCardListForVoice = createSelector(
  getcopilotState,
  (state) => state?.copilotCardListForVoice
);

export const getCopilotCardListForEmail = createSelector(
  getcopilotState,
  (state) => state?.copilotCardListForEmail
);

export const getSpaceBtnCounterForCopilotChatMessage = createSelector(
  getcopilotState,
  (state) => state?.spaceBtnCounterForCopilotChatMessage
);

export const getSpaceBtnCounterForCopilotCard = createSelector(
  getcopilotState,
  (state) => state?.spaceBtnCounterForCopilotCard
);

export const getSpaceBtnCounterForCopilotVoice = createSelector(
  getcopilotState,
  (state) => state?.spaceBtnCounterForCopilotVoice
);

export const getSpaceBtnCounterForCopilotEmail = createSelector(
  getcopilotState,
  (state) => state?.spaceBtnCounterForCopilotEmail
);

export const getIsCopilotSummarySubmitted = createSelector(
  getcopilotState,
  (state) => state.isCopilotSummarySubmitted
);

export const getLoadAutoSummaryState = createSelector(
  getcopilotState,
  (state) => state.loadAutoSummaryState
);

export const getShowSummaryLoadingAnimation = createSelector(
  getcopilotState,
  (state) => state.showSummaryLoadingAnimation
);

export const getShowAgentsForInteractionTransfer = createSelector(
  getcopilotState,
  (state) => state.showAgentsForInteractionTransfer
);

export const getIsSuggestionClicked = createSelector(
  getcopilotState,
  (state) => state.isSuggestionClicked
);

export const getNoOfMsgInCopilotChat = createSelector(
  getcopilotState,
  (state) => state.noOfMsgInCopilotChat
);

export const getCopilotSuggestionCardSendBtnDisableState = createSelector(
  getcopilotState,
  (state) => state.disableCopilotSuggestionCardSent
);

export const getSentimentStateForCopilot = createSelector(
  getcopilotState,
  (state) => state.sentiment
);
export const getSelectedCopilotAssets = createSelector(
  getcopilotState,
  (state) => state.selectedCopilotAssets
);

export const getLoadCopilotInteractionAnimation = createSelector(
  getcopilotState,
  (state) => state.loadCopilotInteractionAnimation
);

export const getshowConversationTopicsState = createSelector(
  getcopilotState,
  (state) => state.showConversationTopics
);

export const getConversationTopics = createSelector(
  getcopilotState,
  (state) => state.conversationTopics
);
